import React from 'react';
import { useNavigate } from 'react-router-dom';
import { ArrowLeft, FileText, Send, MousePointer, Calendar, CheckCircle, Mail, MessageSquare, Zap } from 'lucide-react';

const QuoteAcceptanceGuide = () => {
  const navigate = useNavigate();

  return (
    <div className="p-4 lg:p-8 pb-20 lg:pb-8">
      <div className="max-w-4xl mx-auto">
        {/* Back Button */}
        <button
          onClick={() => navigate('/help')}
          className="flex items-center gap-2 text-gray-600 hover:text-gray-900 mb-6 transition-colors"
        >
          <ArrowLeft size={20} />
          <span>Back to Help Center</span>
        </button>

        {/* Header */}
        <div className="mb-8">
          <div className="flex items-center gap-3 mb-3">
            <div className="p-3 bg-purple-100 rounded-xl">
              <FileText className="text-purple-600" size={32} />
            </div>
            <div>
              <h1 className="text-3xl font-bold text-gray-900">Online Quote Acceptance</h1>
              <p className="text-lg text-gray-600">Let customers accept quotes and schedule service online</p>
            </div>
          </div>
        </div>

        {/* Introduction */}
        <div className="bg-gradient-to-r from-purple-50 to-violet-50 border border-purple-200 rounded-lg p-6 mb-8">
          <div className="flex items-start gap-3">
            <Zap className="text-purple-600 flex-shrink-0 mt-1" size={24} />
            <div>
              <h2 className="text-xl font-bold text-gray-900 mb-2">Close Deals While You Sleep</h2>
              <p className="text-gray-700">
                When you send a quote email, customers get a beautiful, branded page where they can view 
                the quote details, pick their preferred date, and accept - all without calling or texting you. 
                You get notified instantly, and the job is automatically scheduled!
              </p>
            </div>
          </div>
        </div>

        {/* How It Works */}
        <div className="bg-white rounded-lg border border-gray-200 shadow-sm overflow-hidden mb-8">
          <div className="bg-gray-50 px-6 py-4 border-b border-gray-200">
            <h2 className="text-xl font-bold text-gray-900">How It Works</h2>
          </div>
          <div className="p-6">
            <div className="space-y-6">
              {/* Step 1 */}
              <div className="flex items-start gap-4">
                <div className="w-12 h-12 bg-purple-100 rounded-full flex items-center justify-center flex-shrink-0">
                  <Send className="text-purple-600" size={24} />
                </div>
                <div>
                  <h3 className="font-semibold text-gray-900 mb-1">1. You Send the Quote Email</h3>
                  <p className="text-sm text-gray-600">
                    From the job details page, click "Send Quote Email". The customer receives a professional 
                    email with your branding and a "View Quote & Schedule" button.
                  </p>
                </div>
              </div>

              {/* Step 2 */}
              <div className="flex items-start gap-4">
                <div className="w-12 h-12 bg-purple-100 rounded-full flex items-center justify-center flex-shrink-0">
                  <MousePointer className="text-purple-600" size={24} />
                </div>
                <div>
                  <h3 className="font-semibold text-gray-900 mb-1">2. Customer Clicks the Link</h3>
                  <p className="text-sm text-gray-600">
                    They see a beautiful quote page with your logo, the service details, pricing breakdown, 
                    and property address. No login required - the link is secure and unique to their quote.
                  </p>
                </div>
              </div>

              {/* Step 3 */}
              <div className="flex items-start gap-4">
                <div className="w-12 h-12 bg-purple-100 rounded-full flex items-center justify-center flex-shrink-0">
                  <Calendar className="text-purple-600" size={24} />
                </div>
                <div>
                  <h3 className="font-semibold text-gray-900 mb-1">3. Customer Picks a Date</h3>
                  <p className="text-sm text-gray-600">
                    They select their preferred service date from a calendar and optionally choose a time 
                    preference (Morning, Afternoon, or Flexible). They can also add notes for you.
                  </p>
                </div>
              </div>

              {/* Step 4 */}
              <div className="flex items-start gap-4">
                <div className="w-12 h-12 bg-green-100 rounded-full flex items-center justify-center flex-shrink-0">
                  <CheckCircle className="text-green-600" size={24} />
                </div>
                <div>
                  <h3 className="font-semibold text-gray-900 mb-1">4. Quote Accepted!</h3>
                  <p className="text-sm text-gray-600 mb-3">
                    When they click "Accept Quote & Schedule", several things happen automatically:
                  </p>
                  <ul className="space-y-2 text-sm text-gray-600">
                    <li className="flex items-center gap-2">
                      <CheckCircle size={16} className="text-green-600" />
                      Job status changes to "Scheduled"
                    </li>
                    <li className="flex items-center gap-2">
                      <CheckCircle size={16} className="text-green-600" />
                      Scheduled date is set to their selection
                    </li>
                    <li className="flex items-center gap-2">
                      <CheckCircle size={16} className="text-green-600" />
                      Customer receives a confirmation email
                    </li>
                    <li className="flex items-center gap-2">
                      <CheckCircle size={16} className="text-green-600" />
                      You receive an email notification
                    </li>
                    <li className="flex items-center gap-2">
                      <CheckCircle size={16} className="text-green-600" />
                      You receive an SMS (if enabled)
                    </li>
                  </ul>
                </div>
              </div>
            </div>
          </div>
        </div>

        {/* What the Customer Sees */}
        <div className="bg-white rounded-lg border border-gray-200 shadow-sm overflow-hidden mb-8">
          <div className="bg-gray-50 px-6 py-4 border-b border-gray-200">
            <h2 className="text-xl font-bold text-gray-900">What the Customer Sees</h2>
          </div>
          <div className="p-6">
            <p className="text-sm text-gray-600 mb-4">
              The quote acceptance page is designed to look professional and match your branding:
            </p>
            <div className="grid md:grid-cols-2 gap-4">
              <div className="bg-gray-50 rounded-lg p-4 border border-gray-200">
                <h3 className="font-semibold text-gray-900 mb-2">Quote Details</h3>
                <ul className="space-y-1 text-sm text-gray-600">
                  <li>• Your business logo</li>
                  <li>• Customer name & address</li>
                  <li>• Service breakdown with pricing</li>
                  <li>• Total quote amount</li>
                  <li>• Quote expiration date</li>
                </ul>
              </div>
              <div className="bg-gray-50 rounded-lg p-4 border border-gray-200">
                <h3 className="font-semibold text-gray-900 mb-2">Scheduling Options</h3>
                <ul className="space-y-1 text-sm text-gray-600">
                  <li>• Calendar date picker</li>
                  <li>• Time preference selector</li>
                  <li>• Notes field for special requests</li>
                  <li>• Clear call-to-action button</li>
                  <li>• Your contact info for questions</li>
                </ul>
              </div>
            </div>
          </div>
        </div>

        {/* Sending Quote Emails */}
        <div className="bg-white rounded-lg border border-gray-200 shadow-sm overflow-hidden mb-8">
          <div className="bg-gray-50 px-6 py-4 border-b border-gray-200">
            <h2 className="text-xl font-bold text-gray-900 flex items-center gap-2">
              <Mail className="text-blue-600" size={24} />
              How to Send a Quote Email
            </h2>
          </div>
          <div className="p-6">
            <ol className="space-y-4">
              <li className="flex items-start gap-3">
                <span className="w-8 h-8 bg-primary-100 rounded-full flex items-center justify-center text-primary-700 font-bold flex-shrink-0">1</span>
                <div>
                  <p className="font-medium text-gray-900">Create a job with "Quoted" status</p>
                  <p className="text-sm text-gray-600">Add the services and pricing you want to quote</p>
                </div>
              </li>
              <li className="flex items-start gap-3">
                <span className="w-8 h-8 bg-primary-100 rounded-full flex items-center justify-center text-primary-700 font-bold flex-shrink-0">2</span>
                <div>
                  <p className="font-medium text-gray-900">Make sure the customer has an email address</p>
                  <p className="text-sm text-gray-600">You can't send quote emails without one</p>
                </div>
              </li>
              <li className="flex items-start gap-3">
                <span className="w-8 h-8 bg-primary-100 rounded-full flex items-center justify-center text-primary-700 font-bold flex-shrink-0">3</span>
                <div>
                  <p className="font-medium text-gray-900">Click "Send Quote Email" on the job</p>
                  <p className="text-sm text-gray-600">Find this button in the job details or on the job card</p>
                </div>
              </li>
              <li className="flex items-start gap-3">
                <span className="w-8 h-8 bg-primary-100 rounded-full flex items-center justify-center text-primary-700 font-bold flex-shrink-0">4</span>
                <div>
                  <p className="font-medium text-gray-900">Done!</p>
                  <p className="text-sm text-gray-600">The customer receives an email within seconds</p>
                </div>
              </li>
            </ol>
          </div>
        </div>

        {/* Quote Expiration */}
        <div className="bg-amber-50 border border-amber-200 rounded-lg p-6 mb-8">
          <h2 className="text-xl font-bold text-gray-900 mb-3 flex items-center gap-2">
            ⏰ Quote Expiration
          </h2>
          <p className="text-sm text-gray-700 mb-3">
            Quotes are valid for <strong>30 days</strong> by default. After that, the acceptance link expires 
            and customers see a friendly message asking them to contact you for updated pricing.
          </p>
          <p className="text-sm text-gray-700">
            This protects you from customers accepting old quotes when material costs may have changed!
          </p>
        </div>

        {/* Pro Tips */}
        <div className="bg-blue-50 border border-blue-200 rounded-lg p-6 mb-8">
          <h2 className="text-xl font-bold text-gray-900 mb-4 flex items-center gap-2">
            💡 Pro Tips
          </h2>
          <div className="space-y-3">
            <div className="flex items-start gap-3">
              <CheckCircle size={18} className="text-blue-600 mt-0.5 flex-shrink-0" />
              <p className="text-sm text-gray-700">
                <strong>Enable SMS notifications</strong> - Get a text the moment someone accepts. 
                Call them right away to confirm the exact time!
              </p>
            </div>
            <div className="flex items-start gap-3">
              <CheckCircle size={18} className="text-blue-600 mt-0.5 flex-shrink-0" />
              <p className="text-sm text-gray-700">
                <strong>Add detailed service breakdowns</strong> - Quotes with itemized pricing 
                convert better because customers understand exactly what they're getting.
              </p>
            </div>
            <div className="flex items-start gap-3">
              <CheckCircle size={18} className="text-blue-600 mt-0.5 flex-shrink-0" />
              <p className="text-sm text-gray-700">
                <strong>Follow up if they don't accept</strong> - If a quote hasn't been accepted 
                after a few days, call or text the customer. Sometimes they just forgot!
              </p>
            </div>
            <div className="flex items-start gap-3">
              <CheckCircle size={18} className="text-blue-600 mt-0.5 flex-shrink-0" />
              <p className="text-sm text-gray-700">
                <strong>Upload your logo</strong> - Go to Settings → Branding to add your logo. 
                It appears on all quote emails and acceptance pages.
              </p>
            </div>
          </div>
        </div>

        {/* FAQ */}
        <div className="bg-white rounded-lg border border-gray-200 shadow-sm p-6 mb-8">
          <h2 className="text-xl font-bold text-gray-900 mb-4">Frequently Asked Questions</h2>
          <div className="space-y-4">
            <div>
              <h3 className="font-semibold text-gray-900 mb-1">What if the customer wants to change the date after accepting?</h3>
              <p className="text-sm text-gray-600">
                They'll need to contact you directly. You can update the scheduled date in the job details anytime.
              </p>
            </div>
            <div>
              <h3 className="font-semibold text-gray-900 mb-1">Can customers accept quotes without picking a date?</h3>
              <p className="text-sm text-gray-600">
                No, they must select a date to accept. This ensures every accepted quote has a scheduled date 
                so nothing falls through the cracks.
              </p>
            </div>
            <div>
              <h3 className="font-semibold text-gray-900 mb-1">What if I need to change the quote after sending?</h3>
              <p className="text-sm text-gray-600">
                Update the job with the new pricing, then send a new quote email. The old link will still work 
                until the customer uses it or it expires.
              </p>
            </div>
            <div>
              <h3 className="font-semibold text-gray-900 mb-1">Can I resend the quote email?</h3>
              <p className="text-sm text-gray-600">
                Yes! Click "Send Quote Email" again anytime. This is useful if the customer lost the email 
                or you want to remind them.
              </p>
            </div>
            <div>
              <h3 className="font-semibold text-gray-900 mb-1">Does accepting a quote create an invoice?</h3>
              <p className="text-sm text-gray-600">
                No, it just schedules the job. After you complete the work, you'll create the invoice 
                from the Invoices page as usual.
              </p>
            </div>
          </div>
        </div>

        {/* CTA */}
        <div className="bg-gradient-to-r from-primary-50 to-primary-100 border border-primary-200 rounded-lg p-6 text-center mb-8">
          <h3 className="text-xl font-bold text-gray-900 mb-2">Ready to Send Your First Quote?</h3>
          <p className="text-gray-700 mb-4">
            Create a job, add your services, and send the quote email. It's that easy!
          </p>
          <button
            onClick={() => navigate('/jobs')}
            className="btn btn-primary"
          >
            Go to Jobs
          </button>
        </div>

        {/* Back to Help Center */}
        <div className="text-center">
          <button
            onClick={() => navigate('/help')}
            className="text-primary-600 hover:text-primary-700 font-medium"
          >
            ← Back to Help Center
          </button>
        </div>
      </div>
    </div>
  );
};

export default QuoteAcceptanceGuide;